import { lazy, Suspense } from "react";
import { HelmetProvider } from "react-helmet-async";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import Index from "./pages/Index";
import ScrollToHash from "./components/ScrollToHash";
import TrackingMount from "./hooks/useTrackingListener";
import RouteSeo from "./components/RouteSeo";

// Lazy-loaded components
import { SeoBulkApplyProvider } from "./contexts/SeoBulkApplyContext";
import SeoBulkApplyIndicator from "./components/admin/SeoBulkApplyIndicator";
const CookieConsent = lazy(() => import("./components/CookieConsent"));
const PrivateEvents = lazy(() => import("./pages/PrivateEvents"));
const NotFound = lazy(() => import("./pages/NotFound"));
const NewsArticle = lazy(() => import("./pages/NewsArticle"));
const AllNews = lazy(() => import("./pages/AllNews"));
const AdminLogin = lazy(() => import("./pages/admin/AdminLogin"));
const ResetPassword = lazy(() => import("./pages/admin/ResetPassword"));
const AdminDashboard = lazy(() => import("./pages/admin/AdminDashboard"));
const AdminSections = lazy(() => import("./pages/admin/AdminSections"));
const AdminMenus = lazy(() => import("./pages/admin/AdminMenus"));
const AdminNews = lazy(() => import("./pages/admin/AdminNews"));
const AdminSettings = lazy(() => import("./pages/admin/AdminSettings"));
const AdminSocial = lazy(() => import("./pages/admin/AdminSocial"));
const AdminContact = lazy(() => import("./pages/admin/AdminContact"));
const AdminLegal = lazy(() => import("./pages/admin/AdminLegal"));
const AdminEvents = lazy(() => import("./pages/admin/AdminEvents"));
const AdminNewsletter = lazy(() => import("./pages/admin/AdminNewsletter"));
const AdminBranding = lazy(() => import("./pages/admin/AdminBranding"));
const AdminLayout = lazy(() => import("./components/admin/AdminLayout"));
const LegalMentions = lazy(() => import("./pages/LegalMentions"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));
const WpRedirect = lazy(() => import("./pages/WpRedirect"));
const MenuPage = lazy(() => import("./pages/MenuPage"));
const Faq = lazy(() => import("./pages/Faq"));
const AdminReservations = lazy(() => import("./pages/admin/AdminReservations"));
const AdminSeo = lazy(() => import("./pages/admin/AdminSeo"));
const AdminPopups = lazy(() => import("./pages/admin/AdminPopups"));
const EventPopup = lazy(() => import("./components/EventPopup"));
const AdminFaq = lazy(() => import("./pages/admin/AdminFaq"));
const ProtectedRoute = lazy(() => import("./components/admin/ProtectedRoute"));

const queryClient = new QueryClient();

const App = () => (
  <HelmetProvider>
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <BrowserRouter>
          <RouteSeo />
          <SeoBulkApplyProvider>
            <ScrollToHash />
            <TrackingMount />
            <Suspense fallback={<div className="min-h-screen" />}>
              <Routes>
                <Route path="/" element={<Index />} />
                <Route path="/actualites" element={<AllNews />} />
                <Route path="/actualites/:slug" element={<NewsArticle />} />
                <Route path="/evenements-prives" element={<PrivateEvents />} />
                <Route path="/menu" element={<MenuPage />} />
                <Route path="/faq" element={<Faq />} />
                <Route path="/mentions-legales" element={<LegalMentions />} />
                <Route path="/politique-de-confidentialite" element={<PrivacyPolicy />} />
                <Route path="/wp-content/uploads/2025/07/MENU-LPC-Restaurant.pdf" element={<WpRedirect />} />
                <Route path="/wp-content/uploads/2025/07/CARTE-LPC-BOISSONS-VINS.pdf" element={<WpRedirect />} />
                <Route path="/wp-content/uploads/2025/07/menu-boisson-LPC.pdf" element={<WpRedirect />} />
                <Route path="/admin/login" element={<AdminLogin />} />
                <Route path="/reset-password" element={<ResetPassword />} />
                <Route
                  path="/admin"
                  element={
                    <ProtectedRoute>
                      <AdminLayout />
                    </ProtectedRoute>
                  }
                >
                  <Route index element={<AdminDashboard />} />
                  <Route path="sections" element={<AdminSections />} />
                  <Route path="menus" element={<AdminMenus />} />
                  <Route path="news" element={<AdminNews />} />
                  <Route path="social" element={<AdminSocial />} />
                  <Route path="contact" element={<AdminContact />} />
                  <Route path="settings" element={<AdminSettings />} />
                  <Route path="legal" element={<AdminLegal />} />
                  <Route path="events" element={<AdminEvents />} />
                  <Route path="newsletter" element={<AdminNewsletter />} />
                  <Route path="branding" element={<AdminBranding />} />
                  <Route path="reservations" element={<AdminReservations />} />
                  <Route path="seo" element={<AdminSeo />} />
                  <Route path="faq" element={<AdminFaq />} />
                  <Route path="popups" element={<AdminPopups />} />
                </Route>
                <Route path="*" element={<NotFound />} />
              </Routes>
            </Suspense>
            <Suspense fallback={null}>
              <CookieConsent />
              <EventPopup />
            </Suspense>
            <SeoBulkApplyIndicator />
          </SeoBulkApplyProvider>
        </BrowserRouter>

      </TooltipProvider>
    </QueryClientProvider>
  </HelmetProvider>
);

export default App;
